import { IAttributeDoorsFilter } from "helpers/server-rollup";
import { IAttributeFilters } from "./../../product-collection-toolbar/product-collection-toolbar-filters/types";
import lodashGet from "lodash/get";
import lodashSet from "lodash/set";
import { List } from "immutable";
import {
    AggFuncs,
    ATTRIBUTE_GROUP_DELIMITER,
    IMAGE_COLUMN_MIN_WIDTH,
    Item,
    ROW_HEIGHT,
    SizeTableTypes,
    UnitsEntryType,
} from "constants/product-collection";
import { distributeEvenly, isValidNumber, safeParseInt } from "lib/number";
import {
    checkRequiredDesired,
    IDiffDeptChecker,
    ISchemaKey,
    ISchemaToGridParams,
    schemaToGrid,
    getSortOrderForAllocationDimensionValues,
    buildSpreadCurve,
    buildTotalSpreadCurve,
} from "helpers/product-collection";
import {
    AllocationBag,
    AllocationBagType,
    getAllocationBagPath,
    getAllocationBagValue,
    getAllocationDimensionValueGetter,
    IAllocationDimension,
    IAllocationDimensionValue,
    matchBreadcrumbs,
    setBagValue,
} from "helpers/allocation-dimension";
import {
    getAttributeFilters,
    getColIdByColType,
    getDimensionFilters,
    getDoorsGroupColumns,
    ICreatorParams,
    IDoor,
    legacy_parseDimensionParams,
    SR_CUSTOM_DOOR_GROUPING_DELIMITER,
    isDoorInClassification,
} from "helpers/door-collection";

import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { getTableValueSetter, IBagTable, saveToTable, tableValueGetter } from "helpers/table-bag";
import { ISuggestionItem, ISuggestionObject } from "containers/product-collections/product-collection-cart";
import {
    buildPathBasedOnType,
    getDisplayPlansOrTotals,
    getPlanRowType,
    ICurrency,
    PlanMetricRows,
} from "./grid-renderers/pinned-row-renderers";
import {
    CheckboxSelectionCallback,
    ColDef,
    ColGroupDef,
    ColumnApi,
    ColumnGroup,
    HeaderCheckboxSelectionCallback,
    InitialGroupOrderComparatorParams,
    RowNode,
    TabToNextCellParams,
    ValueSetterParams,
} from "ag-grid-community";
import { DIMENSION_IDS_DELIMITER } from "epics/collections";
import { parseMetricReviewPlanValue, parseReviewPlanValue } from "epics/collections/helpers";
import { formatMessage } from "lib/globalize-util";
import { IGridParamsQueryParam, IPreference } from "./tool-panels";
import { IGridContext } from "./product-collection-grid-view";
import orderBy from "lodash/orderBy";
import { DEFAULT_VALUES_PINNED_ROW } from "./detail-list/helpers";
import memoizeOne from "memoize-one";
import { ValueGetterParams } from "ag-grid-community/dist/lib/entities/colDef";
import { FeatureFlags } from "helpers/feature-flags";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import SpreadCurveWorker from "workers/spread-curve/spread-curve.worker";
import { waitForMessage } from "workers/helpers";
import { Distribution } from "actions/collections/collection-settings";
import { getFirstSelectedTargetMetricKind } from "reducers/selectors/collection";
import { getMetricColIdSuffix } from "helpers/metrics/column-formula/ag-grid-helper";
import { cloneDeep } from "lodash";

export enum ColumnTypes {
    DoorBulkQuantity = "door_bulk_quantity",
    AllocationQuantity = "allocation_quantity",
    AllocationDimensionTotal = "allocation_dimension_total",
    ClassificationTotal = "classification_total",
    AllocationDimension = "allocation_dimension",
    AllDimensionTotal = "all_dimension_total",
    NestedDimensionTotal = "nested_dimension_total",
    SizesTotal = "sizes_total",
    TitleColumn = "title_column",
    AttributeColumn = "attribute_column",
    DateColumn = "date_column",
    Metrics = "metrics_column",
}

export enum MetricsTotalType {
    GrandTotalMetric = "grand_total_metric",
    TotalMetric = "total_metric",
}

export enum CloseoutColumns {
    TotalAvailableUnits = "_total_available_units",
    AvailableUnitsBySize = "_available_units_by_size",
}

export enum ColumnOrderTypes {
    Asc = "asc",
    Desc = "desc",
    None = "none",
}

export interface IGetColumnsFromSchemaArg {
    itemDisplayProperties: ISchemaKey[];
    params: ISchemaToGridParams;
    allocationEnabled: boolean;
}

/**
 * Generates ag-grid column objects from item display properties and ag-grid's grid params
 */
export const getColumnsFromSchema = ({ itemDisplayProperties, params }: IGetColumnsFromSchemaArg): ColDef[] => {
    const options = { rowLevelConstraint: checkRequiredDesired(itemDisplayProperties) };
    return itemDisplayProperties.reduce((acc, schemaKey) => {
        const column = schemaToGrid({ schemaKey, params }, options);
        if (column) {
            acc.push(column);
        }
        return acc;
    }, []);
};

export const getPlanMetricsValue = (colDef: ColDef, data: any, rowIndex: number, gridContext: IGridContext) => {
    const { getPlanHelper, getTotalHelper } = colDef.cellRendererParams || {};
    const planRowType = getPlanRowType(rowIndex);

    if (planRowType === undefined || !data || !getPlanHelper || !getTotalHelper) {
        return undefined;
    }

    const total = getTotalHelper(data, gridContext);
    const plan = getPlanHelper(data, gridContext);

    switch (planRowType) {
        case PlanMetricRows.Plan:
            return plan;
        case PlanMetricRows.Diff:
            return +(plan - total).toFixed(1);
        case PlanMetricRows.Total:
            return total;
        default:
            return undefined;
    }
};

export const getTotalMetricsValue = (colDef: ColDef, data: any, gridContext: IGridContext) => {
    const { getTotalHelper } = colDef.cellRendererParams || {};

    if (!data || !getTotalHelper) {
        return undefined;
    }

    return getTotalHelper(data, gridContext);
};

export const setPlanMetricsValue = (colDef: ColDef, data: any, node: RowNode, newValue: string | number) => {
    const planType = getPlanRowType(node.rowIndex);
    if (planType === PlanMetricRows.Plan) {
        const totalType = data.totalType;
        const { type, cellRendererParams } = colDef;
        const { groupId, doorId } = cellRendererParams;
        if (type === ColumnTypes.DoorBulkQuantity) {
            const dimenstionPath = buildPathBasedOnType(doorId, groupId).join(DIMENSION_IDS_DELIMITER);
            if (colDef.cellRendererParams.metric && data.reviewPlan) {
                const kind = colDef.cellRendererParams.metric.kind;
                const value = parseMetricReviewPlanValue(newValue, colDef.cellRendererParams.metric.kind);
                const metricsArray = lodashGet(data.reviewPlan, `${dimenstionPath}.struct_value.metrics`);
                let metricIndex;
                if (metricsArray) {
                    for (const [i, metric] of metricsArray.entries()) {
                        if (metric.kind === kind) {
                            metricIndex = i;
                            break;
                        }
                    }
                }

                if (metricIndex !== undefined) {
                    // metrics array exists with the same metric
                    lodashSet(
                        data.reviewPlan,
                        `${dimenstionPath}.struct_value.metrics[${metricIndex}].value.number_value`,
                        value
                    );
                } else if (metricsArray) {
                    // metrics array exists but there isn't a matching metric
                    metricsArray.push({ kind, value: { number_value: value } });
                } else {
                    // there's no metrics array we need to create one for the existing stored mutation
                    lodashSet(data.reviewPlan, `${dimenstionPath}.struct_value.metrics`, [
                        { kind, value: { number_value: value } },
                    ]);
                }

                return true;
            } else if (totalType) {
                const value = parseReviewPlanValue(newValue, totalType);
                lodashSet(data.plan, `${dimenstionPath}.struct_value.${totalType}`, value);
                return true;
            }
        }
    }
};

export const groupSortComparator = (params: InitialGroupOrderComparatorParams) => {
    const { nodeA, nodeB } = params;
    const nodeAColDef = nodeA.rowGroupColumn?.getColDef();
    const nodeBColDef = nodeB.rowGroupColumn?.getColDef();
    if (
        nodeAColDef?.type === ColumnTypes.AllocationDimension &&
        nodeBColDef?.type === ColumnTypes.AllocationDimension
    ) {
        if (
            nodeAColDef.cellRendererParams.allocationDimension.id ===
            nodeBColDef.cellRendererParams.allocationDimension.id
        ) {
            return getSortOrderForAllocationDimensionValues(
                [nodeAColDef.cellRendererParams.allocationDimension],
                nodeAColDef.cellRendererParams.allocationDimension.id,
                nodeA.key,
                nodeB.key
            );
        }
    }

    // Default group sorting
    return undefined;
};

export interface IProps {
    allowedValueFields?: any;
    collection?: any;
    allocationDimensions?: IAllocationDimension[];
    allocationDimensionGroups?: IAllocationDimension[];
    dates?: IAllocationDimensionValue[];
    doors?: IDoor[];
    doorClassificationSchema?: ISchemaKey[];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    doorGrouping?: string;
    editable?: boolean;
    dataEntryType?: UnitsEntryType;
    getSuggestionData?: () => ISuggestionObject;
    handleFocusOnCellMap?: any;
    onLinkProductToItem?: (rowId: string) => void;
    onOpenProductModal?: (id: string) => void;
    gridParams?: any;
    itemDisplayProperty?: List<any>;
    setSuggestionData?: (data: ISuggestionItem[], storeKey: string) => void;
    allocationEnabled?: boolean;
    onMutateCollectionItem?: (itemsId, index, item, value?) => void;
    onChangeDefaultItemVisibility?: (e?: MouseEvent) => void;
    showDefaultItem?: boolean;
    collectionState?: any;
    reviewPlan?: any;
    onMutateReviewPlan?: (dimensionPath: string[], totalType: any, value: string | number) => void;
    currentAllocationDimensionView?: IPreference[];
    merchantId: string;
    legacyMerchantId: string;
    sizeTableType?: string;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    diffDeptChecker?: IDiffDeptChecker;
    merchantCurrency?: ICurrency;
    displayPlanMetrics?: boolean;
    displayTotals?: boolean;
    pinnedRowColumnOrder?: any;
    defaultDateFormat?: string;
    isServerSide?: boolean;
    isGridTileView?: boolean;
    collectionId?: string;
    inventoryEnabled?: boolean;
    isRollupMode?: boolean;
    shipmentReviewDetailRenderer?: (params: any) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    flags?: FeatureFlags;
    dimensionToolPanelEnabled?: boolean;
    metricsEnabled?: boolean;
    selectedDistribution?: Distribution;
    isCollapseModeOn?: boolean;
}

interface IGetterOrSetterCreatorParams {
    breadcrumb: string[] | string[][];
    isTotalColumn: boolean;
    groupedDoorsNumber?: number;
    attributeDoors?: IDoor[];
}

export const defaultColumnDefsValueGetterCreator = (
    isAverageUnits: boolean,
    creatorParams: IGetterOrSetterCreatorParams
) => {
    const { breadcrumb, isTotalColumn } = creatorParams;
    let { groupedDoorsNumber } = creatorParams;
    groupedDoorsNumber = isAverageUnits ? (groupedDoorsNumber ?? 0) : undefined;
    return (getterParams) => {
        const { data, colDef, node, context } = getterParams;
        if (node.rowPinned && data?.type !== DEFAULT_VALUES_PINNED_ROW) {
            const { displayPlanMetrics, displayTotals } = getDisplayPlansOrTotals(data);
            if (displayPlanMetrics) {
                return getPlanMetricsValue(colDef, data, node.rowIndex, context);
            } else if (displayTotals) {
                return getTotalMetricsValue(colDef, data, context);
            }
        } else {
            // Row group
            if (!data) {
                return undefined;
            }

            if (context?.allocationBagRef) {
                if (isTotalColumn) {
                    const rowTotal = (breadcrumb as string[][]).reduce((acc, row) => {
                        const countForCol = getAllocationBagValue(
                            AllocationBagType.bulks,
                            context?.allocationBagRef.allocationBag,
                            node.id,
                            row
                        );
                        return acc + (countForCol || 0);
                    }, 0);
                    if (context.dataEntryType === UnitsEntryType.GroupAverage) {
                        // For Macys, it will be GroupAverage where we calculate the average.
                        return rowTotal / breadcrumb.length || undefined;
                    }
                    return rowTotal || undefined;
                }
                return getAllocationBagValue(
                    AllocationBagType.bulks,
                    context?.allocationBagRef.allocationBag,
                    node.id,
                    breadcrumb as string[]
                );
            }

            if (isTotalColumn) {
                const rowTotal = (breadcrumb as string[][]).reduce<number | undefined>((acc, col) => {
                    // If this is proxy row (exploded row), skip showing value for unmatched dimensions
                    if (getterParams.data.proxyIndex && !matchBreadcrumbs(getterParams.data.breadcrumbs, col)) {
                        return acc;
                    }

                    const quantity = tableValueGetter(getterParams.data[Item.BULKS[0]], col);
                    if (quantity) {
                        acc = (acc || 0) + quantity;
                    }
                    return acc;
                }, undefined);

                // For Macys, it will be GroupAverage where we calculate the average.
                if (context.dataEntryType === UnitsEntryType.GroupAverage) {
                    return rowTotal / breadcrumb.length || undefined;
                }
                return rowTotal;
            }

            const originalQuantity = tableValueGetter(data[Item.BULKS[0]], breadcrumb as string[]);

            if (originalQuantity) {
                if (groupedDoorsNumber) {
                    const quotient = originalQuantity / groupedDoorsNumber;
                    return quotient === quotient >>> 0 ? quotient : quotient.toFixed(2);
                } else if (groupedDoorsNumber === 0) {
                    return 0;
                }
            }
            return originalQuantity;
        }
    };
};

export const defaultColumnDefsValueSetterCreator = (
    dataEntryType: UnitsEntryType,
    hasAllocationBag: boolean,
    creatorParams: IGetterOrSetterCreatorParams
) => {
    const { breadcrumb: rawBreadcrumb, isTotalColumn, attributeDoors } = creatorParams;
    let { groupedDoorsNumber } = creatorParams;
    groupedDoorsNumber = dataEntryType === UnitsEntryType.Average ? (groupedDoorsNumber ?? 0) : undefined;
    if (hasAllocationBag) {
        if (isTotalColumn) {
            return (params: ValueSetterParams) => {
                const { data, oldValue, newValue, context, api, column, columnApi, colDef } = params;
                const { onMutateBagValues, distributionTargets, isDistributionEnabled } = context as IGridContext;
                const breadcrumb = !isDistributionEnabled
                    ? rawBreadcrumb
                    : buildVisibleDoorsBreadcrumb(
                          { column, columnApi, colDef, attributeDoors },
                          rawBreadcrumb as string[][]
                      );

                if (oldValue === newValue) {
                    api.hideOverlay();
                    return false;
                }

                // Prevent decimal values
                const isNotDecimal = Number.isInteger(parseFloat(newValue || 0));
                if (!isNotDecimal) {
                    api.hideOverlay();
                    return false;
                }

                const itemId = data?.fields?.id?.string_value;

                let mutatedAllocationBag = context.allocationBagRef.allocationBag;
                try {
                    api.showLoadingOverlay();
                    const worker = new SpreadCurveWorker();

                    worker.postMessage({
                        value: newValue,
                        curve: buildSpreadCurve(distributionTargets, breadcrumb, params.data.fields),
                        options: { id: itemId, entryType: dataEntryType },
                    });
                    waitForMessage(worker, itemId).then((distribution: number[]) => {
                        distribution.forEach((quantity, index) => {
                            const singleBreadcrumb = breadcrumb[index];
                            onMutateBagValues &&
                                onMutateBagValues({
                                    quantity,
                                    dimensionValues: singleBreadcrumb as string[],
                                    rowId: itemId,
                                    bagType: AllocationBagType.bulks,
                                });
                            mutatedAllocationBag = setBagValue(
                                AllocationBagType.bulks,
                                mutatedAllocationBag,
                                itemId,
                                singleBreadcrumb as string[],
                                quantity
                            );
                        });
                        context.allocationBagRef.allocationBag = mutatedAllocationBag;
                    });
                } catch (e) {
                    // We only hide the overlay if there's an error that prevents
                    // the mutations to go through, because we also hide the overlay
                    // when mutations are over inside product-collection-grid-view
                    api.hideOverlay();
                }

                return true;
            };
        }
        return (params) => {
            const { data, oldValue, newValue, context, node, colDef } = params;
            const { onMutateBagValues } = context;
            if (oldValue === newValue) {
                return false;
            }

            const integerValue = Math.floor(newValue); // use only integral part for doors.

            if (node.rowPinned && data?.type !== DEFAULT_VALUES_PINNED_ROW) {
                return setPlanMetricsValue(colDef, data, node, newValue);
            }

            const itemId = data?.fields?.id?.string_value;
            onMutateBagValues({
                quantity: integerValue,
                rowId: itemId,
                dimensionValues: rawBreadcrumb,
                bagType: AllocationBagType.bulks,
            });
            context.allocationBagRef.allocationBag = setBagValue(
                AllocationBagType.bulks,
                context.allocationBagRef.allocationBag,
                itemId,
                rawBreadcrumb as string[],
                integerValue
            );

            return true;
        };
    }

    if (isTotalColumn) {
        return (setterParams) => {
            const { node, data, newValue } = setterParams;
            const distribution =
                dataEntryType === UnitsEntryType.GroupAverage
                    ? new Array(rawBreadcrumb.length).fill(newValue)
                    : distributeEvenly(newValue, rawBreadcrumb.length);

            if (data[Item.BULKS[0]]?.current !== undefined) {
                data[Item.BULKS[0]].current = saveToTable(data[Item.BULKS[0]]?.current, rawBreadcrumb, distribution);
            } else {
                data[Item.BULKS[0]] = saveToTable(data[Item.BULKS[0]], rawBreadcrumb, distribution);
            }

            node.setData(data);
            return true;
        };
    }

    const valueSetter = getTableValueSetter(
        (newTable, setterParams) => {
            const { node, data, colDef, newValue } = setterParams;

            if (node.rowPinned && data?.type !== DEFAULT_VALUES_PINNED_ROW) {
                return setPlanMetricsValue(colDef, data, node, newValue);
            } else {
                data[Item.BULKS[0]] = newTable;
            }
        },
        ({ data }) => data[Item.BULKS[0]],
        rawBreadcrumb as string[]
    );
    if (groupedDoorsNumber || groupedDoorsNumber === 0) {
        return (params: ValueSetterParams) => {
            if ([undefined, null].includes(params.newValue) || params.node.rowPinned) {
                return valueSetter(params);
            }

            return valueSetter({ ...params, newValue: (params.newValue >>> 0) * groupedDoorsNumber }); // We floor here to force the user to enter integral values only, as BE only stores that kind of quantity
        };
    }

    return valueSetter;
};

export const grandTotalColumnValueSetterCreator = (dataEntryType: UnitsEntryType) => {
    return async (params) => {
        const metricKind = params.colDef.cellRendererParams.metric?.kind;
        const columns = params.columnApi.getAllGridColumns();

        const { delieveriesColumnsIds, deliveriesBreadcrumb } = columns.reduce(
            (acc, column) => {
                const colId = column.getColId();
                const regexPattern = new RegExp(`^\\d+_metric\\|${metricKind}\\|$`);
                if (regexPattern.test(colId)) {
                    acc.delieveriesColumnsIds.push(column.getColId());
                    acc.deliveriesBreadcrumb.push(column.getColId().replace(/[^\d]/g, ""));
                }
                return acc;
            },
            { delieveriesColumnsIds: [], deliveriesBreadcrumb: [] }
        );

        const { context, newValue, data, api } = params;
        const { distributionTargets } = context;
        const itemId = data?.fields?.id?.string_value;

        try {
            api.showLoadingOverlay();
            const worker = new SpreadCurveWorker();
            const curve = buildTotalSpreadCurve(distributionTargets, deliveriesBreadcrumb, params.data.fields);
            worker.postMessage({
                value: newValue,
                curve,
                options: { id: itemId, entryType: dataEntryType },
            });

            const distribution: number[] = await waitForMessage(worker, itemId);

            delieveriesColumnsIds.forEach((columnId: string, index: number) => {
                params.node.setDataValue(columnId, distribution[index]);
            });
        } catch (e) {
            // We only hide the overlay if there's an error that prevents
            // the mutations to go through, because we also hide the overlay
            // when mutations are over inside product-collection-grid-view
            api.hideOverlay();
        }

        return true;
    };
};

export const totalColumnValueGetterCreator = (params: ICreatorParams) => {
    return (getterParams) => {
        let quantity = 0;
        const { isTotalColumn, breadcrumb } = params;
        const { data, node, context } = getterParams;

        let doorIdsToLookFor: string[] = [];
        if (!isTotalColumn) {
            const cleanedBreadcrumbs = (breadcrumb as string[]).filter((breadcrumb) => !!breadcrumb);
            doorIdsToLookFor = [cleanedBreadcrumbs[0]];
        } else {
            doorIdsToLookFor = (breadcrumb as string[][]).reduce<string[]>((acc, col) => {
                acc.push(col[0]);
                return acc;
            }, []);
        }

        if (context?.allocationBagRef) {
            const allocationBagPath = getAllocationBagPath(AllocationBagType.bulks, node.id);
            const allocationBagMap = context?.allocationBagRef.allocationBag.getIn(allocationBagPath);

            if (allocationBagMap) {
                for (const [key, value] of allocationBagMap) {
                    const splitTokens = key.split("#");
                    if (splitTokens.length === 2 && doorIdsToLookFor.includes(splitTokens[1])) {
                        const doorStrValue = value.getIn(["string_value"]);
                        if (isValidNumber(doorStrValue)) {
                            quantity += parseInt(doorStrValue, 10);
                        }
                    }
                }
            }
        } else {
            const bulksData = data[Item.BULKS[0]];
            bulksData.r.forEach((entry) => {
                if (entry.c.length === 3) {
                    if (doorIdsToLookFor.includes(entry.c[1]) && isValidNumber(entry.c[2])) {
                        quantity += parseInt(entry.c[2], 10);
                    }
                }
            });
        }

        return quantity > 0 ? quantity : undefined;
    };
};

/**
 * Generates default column definition for ag-grid
 */
export const generateDefaultColumnDefs = (props: IProps, validate: boolean = false) => {
    if (!props.itemDisplayProperty) {
        return [];
    }

    const {
        allocationEnabled,
        allocationDimensionGroups = [],
        allocationDimensions,
        allowedValueFields,
        dates,
        doors,
        doorClassificationSchema,
        dimensionsPanelApplied,
        editable,
        dataEntryType,
        getSuggestionData,
        handleFocusOnCellMap,
        itemDisplayProperty,
        onLinkProductToItem,
        onOpenProductModal,
        onChangeDefaultItemVisibility,
        setSuggestionData,
        gridParams,
        reviewPlan,
        currentAllocationDimensionView,
        merchantId,
        sizeTableType,
        merchantPurchaseOrderDoorGroups,
        diffDeptChecker,
        merchantCurrency,
        displayPlanMetrics,
        displayTotals,
        defaultDateFormat,
        showDefaultItem,
        isServerSide,
        isGridTileView,
        collectionId,
        inventoryEnabled,
        isRollupMode,
        allocationBagRef,
        dimensionToolPanelEnabled,
        metricsEnabled,
        flags = {},
        isCollapseModeOn,
    } = props;
    const dimensionFilters = getDimensionFilters(gridParams);
    const attributeFilters = getAttributeFilters(gridParams);
    const isolatedMetric = isCollapseModeOn && getFirstSelectedTargetMetricKind(dimensionsPanelApplied);

    const handleCheckboxSelection = (canEdit): CheckboxSelectionCallback => {
        return (params) => {
            return !!params.data && canEdit;
        };
    };

    const handleHeaderCheckboxSelection = (canEdit): HeaderCheckboxSelectionCallback => {
        return (params) => {
            const columnGroups = params.columnApi.getRowGroupColumns();
            return !columnGroups.length && canEdit;
        };
    };
    const rawItemDisplayProperty = itemDisplayProperty.toJS();

    const { attributeHiddenValues, attributeDirection, legacyPlanMetrics } =
        legacy_parseDimensionParams(dimensionsPanelApplied);
    const showDoors = dimensionsPanelApplied.showDoors;
    const activeAttributes = dimensionsPanelApplied.attributes.filter((attribute) => attribute.selected);
    const collapsedGroupIds = dimensionsPanelApplied.collapsedGroupIds;

    const params = {
        gridParams,
        handleFocusOnCellMap,
        handleRowDrag: editable,
        // if collectionId is undefined, meaning it's a cart grid view, checkbox in the header cell is always shown
        handleHeaderCheckboxSelection: collectionId
            ? handleHeaderCheckboxSelection(isRollupMode ? editable : true)
            : flags.legacyCartEditableSizingGrid,
        // if collectionId is undefined, meaning it's a cart grid view, data row checkboxes are rendered by the cell renderer
        handleCheckboxSelection: collectionId ? handleCheckboxSelection(isRollupMode ? editable : true) : false,
        openDetailModal: onOpenProductModal,
        onLinkProductToItem,
        onChangeDefaultItemVisibility,
        showDefaultItem,
        canLinkProduct: editable,
        allowedValueFields,
        getSuggestionData,
        setSuggestionData,
        validate,
        gridEditable: editable,
        rawItemDisplayProperty,
        merchantId,
        allocationDimensions,
        dimensionFilters,
        attributeFilters,
        dimensionPlanMetrics: legacyPlanMetrics,
        diffDeptChecker,
        displayPlanMetrics,
        displayTotals,
        defaultDateFormat,
        isServerSide,
        isGridTileView,
        collectionId,
        inventoryEnabled,
        isRollupMode,
        enableImageHeaderMenu: collectionId && showDoors,
    };

    const columns = getColumnsFromSchema({
        itemDisplayProperties: rawItemDisplayProperty,
        params,
        allocationEnabled,
    });

    const assortmentDimensionMetrics = metricsEnabled ?? false;

    (allocationDimensions || []).forEach((allocationDimension, index) => {
        const rowGroupIndex = gridParams?.rowGroup?.indexOf(allocationDimension.id);
        const colDef: ColDef = {
            headerName: formatMessage("|dimensionName| (Group)", { dimensionName: allocationDimension.name }),
            field: allocationDimension.id,
            colId: allocationDimension.id,
            type: ColumnTypes.AllocationDimension,
            filter: false,
            suppressMenu: true,
            suppressMovable: true,
            editable: false,
            rowGroupIndex: rowGroupIndex > -1 ? rowGroupIndex : undefined,
            valueGetter: getAllocationDimensionValueGetter(index, allocationDimensions, isRollupMode),
            enableRowGroup: assortmentDimensionMetrics ? !showDoors : true,
            enablePivot: false,
            pivotValueColumn: null,
            enableValue: false,
            hide: true,
            cellRenderer: isRollupMode && "defaultRenderer",
            // Don't allow showing this column, it is expensive when being displayed as column. User can see in allocation groups columns.
            lockVisible: true,
            cellRendererParams: {
                allocationDimension,
            },
        };

        columns.push(colDef);
    });

    let additionalColumns: (ColGroupDef | ColDef)[] = [];
    if (
        showDoors &&
        allocationEnabled &&
        !!doors &&
        !!doorClassificationSchema &&
        allocationDimensionGroups.length === 0
    ) {
        const editableIfTableType = editable && sizeTableType !== SizeTableTypes.Grouped;
        const isAverageUnits = dataEntryType === UnitsEntryType.Average;
        additionalColumns = additionalColumns.concat(
            getDoorsGroupColumns({
                globalGroups: dates, // to be able to use the default value
                merchantCurrency,
                doors,
                doorClassificationSchema,
                hiddenGroupIds: attributeHiddenValues,
                groupDirection: attributeDirection,
                collapsedGroupIds,
                delimiter: dimensionsPanelApplied.delimiter,
                editable: editableIfTableType,
                // door-dimension filters
                dimensionFilters,
                attributeFilters,
                // If editable, set to undefined to use default values
                totalEditable: !isAverageUnits && editableIfTableType ? undefined : false,
                reviewPlan,
                currentLayout: currentAllocationDimensionView,
                merchantPurchaseOrderDoorGroups,
                gridParams,
                valueGetterCreator: defaultColumnDefsValueGetterCreator.bind(null, isAverageUnits),
                valueSetterCreator: defaultColumnDefsValueSetterCreator.bind(
                    null,
                    dataEntryType,
                    Boolean(allocationBagRef)
                ),
                isServerSide,
                movable: !dimensionToolPanelEnabled,
                assortmentDimensionMetrics,
                allocationBagRef,
                dimensionsPanelApplied,
                isolatedMetric,
            })
        );

        if (props.pinnedRowColumnOrder && props.pinnedRowColumnOrder.columnOrderType !== ColumnOrderTypes.None) {
            // Collection can have single dimension only (Door only, no delivery. Eg: Nordstrom Designer)
            // and there is no classification group being applied
            if (allocationDimensions.length === 1 && activeAttributes.length === 0) {
                if (props.pinnedRowColumnOrder.row) {
                    const { nonDimensionColumns, dimensionColumns } = additionalColumns.reduce(
                        (acc, column: ColDef) => {
                            if (column.type === ColumnTypes.DoorBulkQuantity) {
                                acc.dimensionColumns.push(column);
                            } else {
                                acc.nonDimensionColumns.push(column);
                            }

                            return acc;
                        },
                        {
                            nonDimensionColumns: [],
                            dimensionColumns: [],
                        }
                    );

                    additionalColumns = nonDimensionColumns.concat(
                        dimensionColumns.sort((a, b) => {
                            const aValue = (a as any).valueGetter({
                                data: props.pinnedRowColumnOrder.row.data,
                                colDef: a,
                                node: props.pinnedRowColumnOrder.row,
                                context: props.pinnedRowColumnOrder.gridContext,
                            });
                            const bValue = (b as any).valueGetter({
                                data: props.pinnedRowColumnOrder.row.data,
                                colDef: b,
                                node: props.pinnedRowColumnOrder.row,
                                context: props.pinnedRowColumnOrder.gridContext,
                            });
                            return props.pinnedRowColumnOrder.columnOrderType === ColumnOrderTypes.Asc
                                ? aValue - bValue
                                : bValue - aValue;
                        })
                    );
                }
            } else {
                const pinnedRowColumnOrder = generateGroupedOrderedColumns(
                    additionalColumns,
                    props,
                    props.pinnedRowColumnOrder.gridContext,
                    props.pinnedRowColumnOrder.row,
                    props.pinnedRowColumnOrder.columnOrderType,
                    allocationDimensions.length === 1
                );

                additionalColumns.forEach((colDef: ColGroupDef) => {
                    if (colDef.groupId) {
                        const orderedColumnsGroup = pinnedRowColumnOrder.data.find(
                            (orderedColumnsGroup) => orderedColumnsGroup.group === colDef.groupId
                        );
                        if (orderedColumnsGroup) {
                            if (colDef.children) {
                                const [total, firstChildColDef, ...children] = colDef.children;
                                const firstChildColGroupDef = firstChildColDef as ColGroupDef;
                                const orderedChildren = [];
                                if (firstChildColGroupDef.children) {
                                    [firstChildColGroupDef, ...children].forEach((childColGroupDef: ColGroupDef) => {
                                        childColGroupDef.children = orderSubGroup(
                                            childColGroupDef,
                                            orderedColumnsGroup.columns,
                                            pinnedRowColumnOrder.columnOrderType
                                        );
                                        orderedChildren.push(childColGroupDef);
                                    });
                                } else {
                                    orderedColumnsGroup.columns.forEach((column) => {
                                        const value = colDef.children.find(
                                            (child) => (child as ColDef).colId === column.colId
                                        );
                                        if (value) {
                                            orderedChildren.push(value);
                                        }
                                    });
                                }
                                colDef.children = [total, ...orderedChildren];
                            }
                        }
                    }
                });
            }
        }
    }

    return columns.concat(additionalColumns);
};

export const orderSubGroup = (
    colDef: ColGroupDef,
    columns: { colId: string; value: any }[],
    columnOrderType: ColumnOrderTypes
): ColDef[] => {
    const children = colDef.children.filter(
        (child) => (child as ColDef).type !== ColumnTypes.ClassificationTotal
    ) as ColDef[];
    const total = colDef.children.find((child) => (child as ColDef).type === ColumnTypes.ClassificationTotal);
    const orderedChildren: ColDef[] = children.sort((a, b) => {
        const aValue = columns.find((col) => col.colId === a.colId).value;
        const bValue = columns.find((col) => col.colId === b.colId).value;
        return columnOrderType === ColumnOrderTypes.Asc ? aValue - bValue : bValue - aValue;
    });
    return [total, ...orderedChildren];
};

export const generateGroupedOrderedColumns = (
    columnDefs: (ColDef | ColGroupDef)[],
    props: IProps,
    gridContext: IGridContext,
    row: RowNode,
    columnOrderType: ColumnOrderTypes,
    singleDimension: boolean
): {
    columnOrderType: ColumnOrderTypes;
    data: { group: string; columns: { colId: string; value: any }[] }[];
} => {
    const groupedOrderedColumns: { group: string; columns: { colId: string; value: any }[] }[] = [];
    const globalGroups = columnDefs.filter((cols: ColGroupDef) => cols.groupId);
    globalGroups.forEach((group: ColGroupDef) => {
        const columnsToOrder = [];
        props.doors.forEach((door) => {
            // In multi-dimension case, group id will be outer group, in many case is Deliveries.
            const breadcrumbs = [!singleDimension ? group.groupId : undefined, door.id];
            const cleanedBreadcrumbs = breadcrumbs.filter((breadcrumb) => !!breadcrumb);
            const colId =
                group.groupId === ColumnTypes.AllDimensionTotal
                    ? getColIdByColType(ColumnTypes.NestedDimensionTotal, [door.id])
                    : getColIdByColType(ColumnTypes.DoorBulkQuantity, cleanedBreadcrumbs);
            const colDef = getColDef(group.children, colId);

            if (colDef) {
                columnsToOrder.push({
                    colId,
                    value:
                        row && typeof colDef.valueGetter === "function"
                            ? colDef.valueGetter({
                                  data: row.data,
                                  colDef,
                                  node: row,
                                  context: gridContext,
                              } as ValueGetterParams)
                            : null,
                });
            }
        });

        groupedOrderedColumns.push({
            group: group.groupId,
            columns:
                columnOrderType === ColumnOrderTypes.None
                    ? columnsToOrder
                    : orderBy(columnsToOrder, ["value"], [columnOrderType]),
        });
    });
    return { data: groupedOrderedColumns, columnOrderType };
};

export const generatePinnedRowId = (totalType: string, planType: string) => {
    return `${totalType}${planType ? `-${planType}` : ""}`;
};

export const getColDef = (groupChildren: (ColDef | ColGroupDef)[], colId: string): ColDef | null => {
    const colDef = groupChildren.find((element: ColDef) => element.colId === colId);
    if (colDef) {
        return colDef;
    } else {
        const colDefWithChildren = groupChildren.filter((x: ColGroupDef) => x.children) as ColGroupDef[];
        for (let i = 0; i < colDefWithChildren.length; i++) {
            const innerColDef = getColDef(colDefWithChildren[i].children, colId);
            if (innerColDef) {
                return innerColDef;
            }
        }
        return null;
    }
};

export const gridAggFunctions = {
    [AggFuncs.AVG.toLocaleLowerCase()]: (values) => {
        let sum = 0;
        values.forEach((num) => {
            num = num === "" ? 0 : num;
            sum = sum + parseFloat(num);
        });
        if (isNaN(sum) || !values.length) {
            return "";
        } else {
            return sum / values.length;
        }
    },
    [AggFuncs.SUM.toLocaleLowerCase()]: (values) => {
        let sum = 0;
        values.forEach((num) => {
            num = num === "" ? 0 : num;
            sum = sum + parseFloat(num);
        });
        if (isNaN(sum) || !values.length) {
            return "";
        } else {
            return sum;
        }
    },
    [AggFuncs.SUM_WITH_PERCENT.toLocaleLowerCase()]: (values) => {
        let sum = 0;
        values.forEach((num) => {
            num = num === "" ? 0 : num;
            sum = sum + parseFloat(num);
        });
        if (isNaN(sum) || !values.length) {
            return "";
        } else {
            return sum;
        }
    },
    [AggFuncs.COUNT.toLocaleLowerCase()]: (values) => {
        return values.length;
    },
    [AggFuncs.COUNT_WITH_PERCENT.toLocaleLowerCase()]: (values) => {
        return values.length;
    },
};

export const getLegacyBulkTotalsSum = (item) => {
    const dictionary = {};
    item.allocations?.r.forEach(({ c }) => {
        const breadcrumb = c.slice(0, c.length - 2);
        const property = breadcrumb.join("-");

        if (property in dictionary) {
            dictionary[property].value += safeParseInt(c[c.length - 1], 0);
        } else {
            dictionary[property] = {
                breadcrumbs: breadcrumb,
                value: safeParseInt(c[c.length - 1], 0),
            };
        }
    });
    return {
        r: Object.keys(dictionary).map((key) => {
            const content = dictionary[key].breadcrumbs;
            content.push(dictionary[key].value.toString());
            return { c: content };
        }),
    };
};

// This method is called every time the image cell column is resized
// the new image cell dimensions are saved globally so it won't change when buyer navigates elsewhere in the assortment tool
export const getNewResizedImageCellDimensions = (columnActualWidth) => {
    const hasMinWidth = columnActualWidth === IMAGE_COLUMN_MIN_WIDTH;
    // Sometimes the imageCell has extra space at top and at bottom for that reason I'm removing 50px
    const extraVerticalSpace = 50;
    const columnWidth = hasMinWidth ? IMAGE_COLUMN_MIN_WIDTH : columnActualWidth;
    const rowHeight = hasMinWidth ? ROW_HEIGHT : columnActualWidth - extraVerticalSpace;

    return {
        width: columnWidth,
        height: rowHeight,
    };
};

// This method calculates the new image's dimensions for the `ImageCellRenderer` after the image Column is resized
export const getNewResizedImageDimensions = (imageCellWidth, defaultImageDimensions) => {
    const scalesBasedOnImageCellWidth = [
        { width: 350, scale: 3 },
        { width: 250, scale: 2.5 },
        { width: 160, scale: 2 },
        { width: 145, scale: 1.5 },
    ];
    const scale = scalesBasedOnImageCellWidth.find(({ width }) => imageCellWidth >= width)?.scale ?? 1;
    const imageWidth = imageCellWidth
        ? Math.round((imageCellWidth * defaultImageDimensions.width * scale) / IMAGE_COLUMN_MIN_WIDTH)
        : defaultImageDimensions.width;
    const imageHeight = Math.round((imageWidth * defaultImageDimensions.height) / defaultImageDimensions.width);

    return {
        width: imageWidth,
        height: imageHeight,
    };
};

export const legacyDeriveSingleSizeAllocationsFromBulks = (
    bulks: IBagTable,
    dates: IAllocationDimensionValue[],
    size: string
) => {
    const basePosition = dates?.length ? 1 : 0;
    return bulks?.r?.map(({ c }) => ({
        c: [...(basePosition ? [c[0]] : []), c[basePosition], size, c[basePosition + 1]],
    }));
};

/**
 * Returns allocation dimension ids based on supplied gridparams with dimension filters.
 * @param allocationDimensions
 * @param gridParams
 * @param dimensionParams
 * @returns {string[] []} - atmost length 3. Index 0 is string[] of delivery dimensions ids. 1 is door and 2 is size.
 */
export const getNewDimensionFilters = (
    allocationDimensions: IAllocationDimension[],
    gridParams: IGridParamsQueryParam,
    dimensionParams: DimensionsPanelReduxState
): string[][] => {
    const dimensionFilters: string[][] = getDimensionFilters(gridParams);
    return allocationDimensions.map((allocationDimension, index) => {
        const dimensionFilter = dimensionFilters[index];
        if (
            !dimensionFilter // We don't want hidden classification to override active filter
        ) {
            // Special treatment when it's lowest dimension to handle door grouping
            if (index === allocationDimensions.length - 1) {
                const { attribute, attributeHiddenValues } = legacy_parseDimensionParams(dimensionParams);

                // Exclude dimension values that are not active and not included (aka !inAllocation)
                // Exclude dimension values whose classifications are hidden.
                // Eg: When A+ and A clusters are hidden, doors classified with A+ and A should be excluded.
                return allocationDimension.dimension_values.reduce((acc, dimensionValue) => {
                    let isHiddenClassificationValues;

                    if (Array.isArray(attributeHiddenValues)) {
                        isHiddenClassificationValues = attributeHiddenValues?.includes(attribute);
                    } else if (typeof attributeHiddenValues === "object") {
                        attribute?.split(ATTRIBUTE_GROUP_DELIMITER)?.forEach((element) => {
                            const [elementKey, collectionId] = element.split(SR_CUSTOM_DOOR_GROUPING_DELIMITER);
                            const fieldKey = collectionId ? elementKey : element;

                            // @ts-expect-error
                            isHiddenClassificationValues = attributeHiddenValues[element]?.some((value) => {
                                const field = dimensionValue.fields?.[fieldKey];
                                return field && field.string_value === value && (!collectionId || field[collectionId]);
                            });
                        });
                    }

                    const isInHiddenClassification = attribute && isHiddenClassificationValues;

                    const isInAssortment = dimensionValue.active && dimensionValue.included;

                    if (
                        !acc.includes(dimensionValue.id) && // prevent duplicate id
                        !isInHiddenClassification && // not hidden
                        isInAssortment // in assortment
                    ) {
                        acc.push(dimensionValue.id);
                    }
                    return acc;
                }, []);
            } else {
                return allocationDimension.dimension_values.reduce((acc, dimensionValue) => {
                    const isInAssortment = dimensionValue.active && dimensionValue.included;

                    if (
                        isInAssortment // in assortment
                    ) {
                        acc.push(dimensionValue.id);
                    }
                    return acc;
                }, []);
            }
        } else {
            return dimensionFilter;
        }
    });
};

export const memoizedGetNewDimensionFilters = memoizeOne(
    (
        allocationDimensions: IAllocationDimension[],
        gridParams: IGridParamsQueryParam,
        dimensionParams: DimensionsPanelReduxState
    ) => getNewDimensionFilters(allocationDimensions, gridParams, dimensionParams)
);

// is there is no attributes filter all doors are returned
export const getDoorsWithinClasifications = (doors: IDoor[], attributeFilters: IAttributeFilters): string[] => {
    return doors.reduce((acc, door) => {
        if (
            isDoorInClassification(door, {
                activeClassification: null,
                hiddenClassificationValues: [],
                attributeFilters,
            })
        ) {
            acc.push(door.id);
        }

        return acc;
    }, []);
};

export const getAttributesDoorsFilter = (doors: IDoor[], attributeFilters: IAttributeFilters) =>
    Object.keys(attributeFilters).reduce<IAttributeDoorsFilter>((acc, key) => {
        acc[key] = getDoorsWithinClasifications(doors, {
            [key]: attributeFilters[key],
        });

        return acc;
    }, {} as IAttributeDoorsFilter);

export interface IFilteredTotals {
    showFilteredTotals: boolean;
    products?: number;
    units?: number;
    retail?: number;
    cost?: number;
}

export const isColumnGroupsExpanded = (columnGroups: ColumnGroup[]): boolean => {
    for (const columnGroup of columnGroups) {
        if (!columnGroup.isExpanded()) {
            return false;
        }
    }

    return true;
};

export const getColumnGroupsAtLevel = (
    columnApi: ColumnApi,
    columnDefs: (ColDef | ColGroupDef)[],
    currentLevel: number,
    targetLevel: number
): ColumnGroup[] => {
    const groupedColumnDefs = columnDefs.filter((colDef) => {
        if ("children" in colDef) {
            return colDef.children && colDef.children.length > 0;
        }

        return false;
    });

    let aggregatedColumnDefs: ColumnGroup[] = [];
    if (currentLevel === targetLevel) {
        groupedColumnDefs.forEach((groupedColumnDef) => {
            if ("groupId" in groupedColumnDef) {
                const columnGroup = columnApi.getColumnGroup(groupedColumnDef.groupId);
                if (columnGroup) {
                    aggregatedColumnDefs.push(columnGroup);
                }
            }
        });
    } else {
        groupedColumnDefs.forEach((groupedColumnDef) => {
            if ("children" in groupedColumnDef) {
                aggregatedColumnDefs = [
                    ...aggregatedColumnDefs,
                    ...getColumnGroupsAtLevel(columnApi, groupedColumnDef.children, currentLevel + 1, targetLevel),
                ];
            }
        });
    }

    return aggregatedColumnDefs;
};

export const getUpdatedSchemaKeysByMovingColumns = ({
    fromIndex,
    toIndex,
    itemDisplayProperty,
}: {
    fromIndex: number;
    toIndex: number;
    itemDisplayProperty: List<Map<string, any>>;
}) => {
    // Create a copy of itemDisplayProperty
    const currentSchemaKeys = itemDisplayProperty.asImmutable();
    // Get schemaKey to be moved
    const movedSchemaKey = currentSchemaKeys.get(fromIndex);
    // Remove item to be moved
    let updatedSchemaKeys = currentSchemaKeys.splice(fromIndex, 1);
    // Insert moved schemaKey to new position
    updatedSchemaKeys = updatedSchemaKeys.splice(toIndex, 0, movedSchemaKey);

    return updatedSchemaKeys;
};

interface BuildVisibleDoorsBreadcrumbParams extends Pick<ValueSetterParams, "column" | "colDef" | "columnApi"> {
    attributeDoors?: IDoor[];
}

export const buildVisibleDoorsBreadcrumb = (
    params: BuildVisibleDoorsBreadcrumbParams,
    allDoorsBreadcrumb: string[][]
) => {
    const { colDef, columnApi, column, attributeDoors } = params;

    const isClassificationTotalColumn = colDef.type === ColumnTypes.ClassificationTotal;
    const metricKind = colDef.cellRendererParams.metric?.kind;
    const columns = columnApi.getAllGridColumns();
    const currentColId = column.getColId();
    const [deliveryId] = currentColId.split("_");
    const visibleColumnsRegex = new RegExp(
        `^${ColumnTypes.DoorBulkQuantity}_${deliveryId}_\\d+_metric\\|${metricKind}\\|$`
    );

    const visibleDoors = columns.reduce((acc, column) => {
        const colId = column.getColId();
        if (visibleColumnsRegex.test(colId)) {
            const [deliveryId, doorId] = column
                .getColId()
                .split("_")
                .filter((v) => !isNaN(Number(v)));

            if (isClassificationTotalColumn) {
                if (attributeDoors.some((door) => door.id === doorId)) {
                    acc.push([deliveryId, doorId]);
                }
                return acc;
            }
            acc.push([deliveryId, doorId]);
        }
        return acc;
    }, []);

    const doorsBreadcrumb = visibleDoors.length ? visibleDoors : allDoorsBreadcrumb;

    return doorsBreadcrumb;
};

export const handleTabToNextCell = (params: TabToNextCellParams) => {
    const { nextCellPosition, previousCellPosition } = params;

    const isLastMetricColumn =
        Boolean(getMetricColIdSuffix(previousCellPosition.column.getColId())) &&
        Boolean(!getMetricColIdSuffix(nextCellPosition?.column.getColId()));

    if (isLastMetricColumn) {
        return handleMetricsColumnsFocusChange(params);
    }

    return nextCellPosition;
};

const handleMetricsColumnsFocusChange = (params: TabToNextCellParams) => {
    const { backwards, nextCellPosition, previousCellPosition, columnApi } = params;

    const isNextRowEqualToPrevious = previousCellPosition.rowIndex === nextCellPosition.rowIndex;
    const valuetoAddToRowIndex = backwards ? -1 : 1;
    const nextRowIndex = !isNextRowEqualToPrevious
        ? nextCellPosition.rowIndex
        : nextCellPosition.rowIndex + valuetoAddToRowIndex;

    const columns = columnApi.getAllDisplayedColumns();
    const metricsCols = columns.filter((column) => Boolean(getMetricColIdSuffix(column.getColId())));
    const firstMetricCol = metricsCols[0];
    const lastMetricCol = metricsCols[metricsCols.length - 1];
    const targetCol = backwards ? lastMetricCol : firstMetricCol;

    const newNextCellPostion = {
        column: targetCol,
        rowIndex: nextRowIndex,
        rowPinned: targetCol?.getPinned(),
    };

    return newNextCellPostion;
};

/* Function to merge the views that are provided from the saved configuration, as well as recently resized configuration */
export const mergeAllocationDimensions = (currentAllocationDimensionView = [], allocationDimensionsColumns = []) => {
    let newAllocationDimensionColumns = [];

    const customAllocationDimensionColumns = cloneDeep(allocationDimensionsColumns);
    currentAllocationDimensionView.forEach((savedView) => {
        customAllocationDimensionColumns.some((updatedView, index, arr) => {
            if (savedView.field_name === updatedView.field_name && updatedView.grid_column?.width) {
                savedView.grid_column.width = updatedView.grid_column.width;
                arr.splice(index, 1);
                return true;
            }
        });
        newAllocationDimensionColumns.push(savedView);
    });
    if (customAllocationDimensionColumns.length > 0) {
        newAllocationDimensionColumns = newAllocationDimensionColumns.concat(customAllocationDimensionColumns);
    }

    return newAllocationDimensionColumns;
};
